import { useState } from 'react';
import {
  Text, Button, Badge, Dialog, DialogTrigger, DialogSurface,
  DialogTitle, DialogBody, DialogActions, DialogContent,
  Input, Dropdown, Option, Field,
} from '@fluentui/react-components';
import { FolderRegular, AddRegular } from '@fluentui/react-icons';
import type { Project } from '../services/project-service';

const PHASE_COLOURS: Record<string, 'brand' | 'success' | 'warning' | 'danger' | 'informative'> = {
  strategy: 'brand',
  plan: 'informative',
  ready: 'warning',
  adopt: 'success',
  complete: 'success',
};

interface ProjectsPageProps {
  projects: Project[];
  onCreateProject: (project: Omit<Project, 'id'>) => void;
  onSelectProject: (project: Project) => void;
}

export function ProjectsPage({ projects, onCreateProject, onSelectProject }: ProjectsPageProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    customerId: '',
    scope: 'Azure Migrate Assessment',
    azureRegion: 'South Africa North',
    hypervisor: 'Hyper-V',
    vpnType: 'site-to-site',
  });

  const handleCreate = () => {
    onCreateProject({
      ...newProject,
      status: 'active',
      cafPhase: 'strategy',
    });
    setDialogOpen(false);
    setNewProject({ name: '', customerId: '', scope: 'Azure Migrate Assessment', azureRegion: 'South Africa North', hypervisor: 'Hyper-V', vpnType: 'site-to-site' });
  };

  return (
    <div className="page-root page-root--scrollable projects-page">
      <div className="page-title-bar">
        <FolderRegular className="icon-title" />
        <Text size={500} weight="semibold">Projects</Text>
        <div className="title-bar-actions">
          <Dialog open={dialogOpen} onOpenChange={(_e, data) => setDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" icon={<AddRegular />}>New Project</Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Create Project</DialogTitle>
                <DialogContent>
                  <div className="dialog-form">
                    <Field label="Customer ID" required>
                      <Input value={newProject.customerId} onChange={(_e, d) => setNewProject(p => ({ ...p, customerId: d.value }))} placeholder="e.g. kzn-health" />
                    </Field>
                    <Field label="Project Name" required>
                      <Input value={newProject.name} onChange={(_e, d) => setNewProject(p => ({ ...p, name: d.value }))} placeholder="e.g. KZN Health Azure Migration" />
                    </Field>
                    <Field label="Scope">
                      <Input value={newProject.scope} onChange={(_e, d) => setNewProject(p => ({ ...p, scope: d.value }))} />
                    </Field>
                    <Field label="Azure Region">
                      <Dropdown value={newProject.azureRegion} onOptionSelect={(_e, d) => setNewProject(p => ({ ...p, azureRegion: d.optionValue ?? '' }))}>
                        <Option value="South Africa North">South Africa North</Option>
                        <Option value="West Europe">West Europe</Option>
                        <Option value="North Europe">North Europe</Option>
                        <Option value="UK South">UK South</Option>
                      </Dropdown>
                    </Field>
                    <Field label="Hypervisor">
                      <Dropdown value={newProject.hypervisor} onOptionSelect={(_e, d) => setNewProject(p => ({ ...p, hypervisor: d.optionValue ?? '' }))}>
                        <Option value="Hyper-V">Hyper-V</Option>
                        <Option value="VMware">VMware</Option>
                      </Dropdown>
                    </Field>
                    <Field label="VPN Type">
                      <Dropdown value={newProject.vpnType} onOptionSelect={(_e, d) => setNewProject(p => ({ ...p, vpnType: d.optionValue ?? '' }))}>
                        <Option value="site-to-site">Site-to-Site VPN</Option>
                        <Option value="expressroute">ExpressRoute</Option>
                        <Option value="direct-internet">Direct Internet</Option>
                      </Dropdown>
                    </Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button appearance="primary" onClick={handleCreate} disabled={!newProject.name || !newProject.customerId}>Create</Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      </div>

      <div className="table-scroll-container">
        <table className="data-table projects-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Customer</th>
              <th>CAF Phase</th>
              <th>Scope</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 && (
              <tr><td colSpan={6} className="text-muted table-empty">No projects yet. Create one to get started.</td></tr>
            )}
            {projects.map(p => (
              <tr key={p.id} onClick={() => onSelectProject(p)} className="clickable">
                <td><Text weight="semibold">{p.name}</Text></td>
                <td className="text-muted">{p.customerId}</td>
                <td><Badge size="small" color={PHASE_COLOURS[p.cafPhase] ?? 'informative'}>{p.cafPhase}</Badge></td>
                <td className="text-muted">{p.scope}</td>
                <td><Badge size="small" appearance="outline" color={p.status === 'active' ? 'success' : 'warning'}>{p.status}</Badge></td>
                <td className="text-muted">{p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-ZA') : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
